import React, { useEffect, useState, useRef } from "react";
import { getStyles } from "@/assets/js/utils/functions";
import { getSizedImage } from "@/assets/js/utils/utils";
import useScreenSize, { BreakPoint } from "@/components/hooks/useScreenSize";
import useSlider from "@/components/hooks/useSlider";
import { StoreCategory, StoreTestimonial } from "@/assets/interfaces";
import CategoryLoader from "@/components/storefronts-v2/items/category-loader";

interface Props {
  reviews: StoreTestimonial[];
}

const StoreReviews: React.FC<Props> = ({ reviews }) => {
  const { screen } = useScreenSize();
  const [categories, setCategories] = useState<StoreCategory[]>([]);
  const { next, slider, slide, previous, slides, currentSlide, switchSlides, hasNext, hasPrevious } = useSlider({
    slides: categories?.length,
    sameWidthAsSlider: false,
    gap: 24,
  });

  // Array of refs for each slide
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);

  const scrollbarTrack = useRef<HTMLDivElement | null>(null);
  const scrollbarHandle = useRef<HTMLDivElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);

  // Calculate the number of visible items based on screen size
  const getVisibleItems = () => {
    if (screen === BreakPoint.xs) return 2; // 48% width
    if (screen === BreakPoint.sm) return 3; // 38% width
    if (screen === BreakPoint.m) return 4; // 29% width
    return 5; // 20% width (lg and above)
  };

  const visibleItems = getVisibleItems();

  // Calculate the approximate item width including the gap
  const itemWidth = slider.current ? slider.current.offsetWidth / visibleItems : 0;

  // Log scrollLeft changes to debug SmoothHorizontalScrolling
  useEffect(() => {
    const sliderEl = slider.current;
    if (sliderEl) {
      const handleScroll = () => {
        // console.log("scrollLeft: ", sliderEl.scrollLeft);
      };
      sliderEl.addEventListener("scroll", handleScroll);
      return () => sliderEl.removeEventListener("scroll", handleScroll);
    }
  }, []);

  // Update scrollbar handle position based on slider scroll
  const updateScrollbar = () => {
    if (slider.current && scrollbarHandle.current && scrollbarTrack.current) {
      const { scrollLeft, scrollWidth, clientWidth } = slider.current;
      const trackWidth = scrollbarTrack.current.offsetWidth;
      const handleWidth = (clientWidth / scrollWidth) * trackWidth;
      const handleLeft = (scrollLeft / scrollWidth) * trackWidth;

      scrollbarHandle.current.style.width = `${handleWidth}px`;
      scrollbarHandle.current.style.transform = `translateX(${handleLeft}px)`;
    }
  };

  const handleTouchStart = (e: React.TouchEvent) => {
    setIsDragging(true);
    setStartX(e.touches[0].pageX); // Use the first touch point
    if (slider.current) {
      setScrollLeft(slider.current.scrollLeft);
    }
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isDragging || !slider.current || !scrollbarTrack.current) return;

    const dx = e.touches[0].pageX - startX; // Calculate the horizontal movement
    const { scrollWidth, clientWidth } = slider.current;
    const trackWidth = scrollbarTrack.current.offsetWidth;
    const scrollWidthRatio = scrollWidth / trackWidth;

    slider.current.scrollLeft = Math.min(Math.max(scrollLeft + dx * scrollWidthRatio, 0), scrollWidth - clientWidth);
  };

  const handleTouchEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    const sliderEl = slider.current;
    if (sliderEl) {
      sliderEl.addEventListener("scroll", updateScrollbar);
      return () => sliderEl.removeEventListener("scroll", updateScrollbar);
    }
  }, []);

  useEffect(() => {
    updateScrollbar(); // Initialize scrollbar position on mount
  }, [categories]);

  if (reviews.length === 0) return null;

  return (
    <section className="py-3 mb-7">
      <div className="w-full block space-y-6 sm:space-y-8 md:space-y-15 px-5 sm:px-9 md:px-12.5">
        <div className="store-container flex justify-between items-center mb-5 sm:mb-5.5 lg:mb-6.5">
          <div className="container flex justify-between items-center">
            <h3 className="text-black text-lg sm:text-xl md:text-xl font-bold">Customer Reviews</h3>

            <div className="hidden sm:flex items-center space-x-3">
              <button
                className={`h-6.25 sm:h-7.5 w-8 sm:w-10 rounded-15 flex items-center justify-center bg-grey-fields-100 text-primary-500`}
                onClick={previous}
                aria-label="previous"
              >
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <path
                    d="M13 7H1"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                  <path
                    d="M7 1L1 7L7 13"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              </button>
              <button
                className={`h-6.25 sm:h-7.5 w-8 sm:w-10 rounded-15 flex items-center justify-center bg-grey-fields-100 text-primary-500`}
                onClick={next}
                aria-label="next"
              >
                <svg width="14" height="14" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M6 0L4.9425 1.0575L9.1275 5.25H0V6.75H9.1275L4.9425 10.9425L6 12L12 6L6 0Z"
                    fill="currentColor"
                  />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>

      <div className="w-full block space-y-6 sm:space-y-8 md:space-y-15">
        <div className="relative left-0 right-0 w-screen">
          <ul
            className="flex overflow-x-auto scrollbar-hide gap-6 md:gap-8 carousel-responsive-padding items-start"
            ref={slider}
          >
            {reviews && reviews.length > 0
              ? reviews.map((i, index) => (
                  <div
                    className="flex-shrink-0 w-full max-w-[350px] bg-grey-fields-200 rounded-15 overflow-hidden store-reviewcard"
                    key={index}
                    ref={(el) => (slideRefs.current[index] = el as HTMLDivElement | null)}
                    style={getStyles({ "--delay": `${index * 0.2}s`, "--from": "15%", "--duration": "1s" })}
                  >
                    <div className="flex items-center gap-3 px-4 pt-4 md:px-6 md:pt-6 pb-2">
                      <div className="text-base sm:text-lg xl:text-lg text-black font-bold font-display">
                        {i.customer_name}
                      </div>
                      <div className="text-base sm:text-lg xl:text-lg text-black-placeholder font-medium font-display ">
                        {i.source}
                      </div>
                    </div>
                    <div className="px-4 pb-4 md:px-6 md:pb-6">
                      <p className="text-black-placeholder text-1xs md:text-sm leading-relaxed">
                        &quot;{i.content}&quot;
                      </p>
                    </div>
                  </div>
                ))
              : Array.from(Array(6).keys()).map((_, index) => <CategoryLoader key={index} />)}
          </ul>
        </div>
      </div>

      <div
        ref={scrollbarTrack}
        className="relative mt-4.5 h-1 bg-grey-fields-200 mx-5 sm:mx-9 md:mx-12.5 sm:hidden"
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
      >
        <div
          ref={scrollbarHandle}
          className="absolute top-0 left-0 h-full bg-black-muted cursor-pointer"
          onTouchStart={handleTouchStart}
          style={{ width: "2%" }}
        ></div>
      </div>
    </section>
  );
};

export default StoreReviews;
